import React, {ReactElement, useMemo, useState} from 'react';

import {StorageCropId} from '../../decision/data/storageAshTransport';
import {useStorageCalculator} from '../../decision/storageCalculator';
import {buildSimpleCalculatorTableRow, buildStorageCropIdRow} from '../../util/calculators.util';
import Select from '../Select';
import SimpleCalculatorCard from './SimpleCalculatorCard';
import SimpleCalculatorTable, {SimpleCalculatorTableCell} from './SimpleCalculatorTable';
import ValueWithUnit from './ValueWithUnit';

type Props = {};

const areaHarvestedOptions = [3, 5, 10, 20, 30].map(area => ({
  label: `${area} ha`,
  value: area,
}));

const mcOptions = [10, 15, 20, 25, 30, 35, 40, 45].map(mc => ({
  label: `${mc} %`,
  value: mc,
}));

const storableHeightOptions = Array(20)
  .fill(null)
  .map((_, i) => i + 1)
  .map(height => ({
    label: `${height} m`,
    value: height,
  }));

const StorageCalculator = ({}: Props): ReactElement => {
  const [areaHarvested, setAreaHarvested] = useState<number>();
  const [cropId, setCropId] = useState<StorageCropId>();
  const [mc, setMc] = useState<number>();
  const [storableHeight, setStorableHeight] = useState<number>();

  const input = {areaHarvested, cropId, mc, storableHeight};
  const {data} = useStorageCalculator(input);

  const areaHarvestedLabel = areaHarvested !== undefined ? `${areaHarvested} ha` : 'Select';
  const mcLabel = mc !== undefined ? `${mc} %` : 'Select';
  const storableHeightLabel = storableHeight !== undefined ? `${storableHeight} m` : 'Select';

  const values = useMemo<SimpleCalculatorTableCell[][]>(
    () => [
      buildSimpleCalculatorTableRow({
        leftContent: 'Area harvested',
        rightContent: (
          <Select
            floating
            variant={'table'}
            buttonContent={areaHarvestedLabel}
            options={areaHarvestedOptions}
            selectedValues={areaHarvested ? [areaHarvested] : []}
            onOptionSelected={value => setAreaHarvested(value)}
            buttonClassName={'rounded-r-2xl'}
          />
        ),
        border: 'both',
        type: 'input',
      }),
      buildStorageCropIdRow({cropId, onSelected: setCropId}),
      buildSimpleCalculatorTableRow({
        leftContent: 'Material form',
        rightContent: <ValueWithUnit value={data?.crop?.materialForm} />,
        border: 'middle',
      }),
      buildSimpleCalculatorTableRow({
        leftContent: 'Typical yield (ODT)',
        rightContent: <ValueWithUnit value={data?.crop?.driedYield} unit={'tonnes/ha'} />,
        border: 'middle',
      }),
      buildSimpleCalculatorTableRow({
        leftContent: 'Bulk Density',
        rightContent: <ValueWithUnit value={data?.crop?.bulkDensity} unit={'tonnes/m3'} />,
        border: 'bottom',
      }),
      buildSimpleCalculatorTableRow({
        leftContent: 'Crop weight (ODT)',
        rightContent: <ValueWithUnit value={data?.cropWeightDried} unit={'tonnes'} />,
      }),
      buildSimpleCalculatorTableRow({
        leftContent: 'MC at point of storage',
        rightContent: (
          <Select
            floating
            variant={'table'}
            buttonContent={mcLabel}
            options={mcOptions}
            selectedValues={mc ? [mc] : []}
            onOptionSelected={value => setMc(value)}
            menuClassName={'max-h-[300px]'}
            buttonClassName={'rounded-r-2xl'}
          />
        ),
        border: 'both',
        type: 'input',
      }),
      buildSimpleCalculatorTableRow({
        leftContent: 'Crop weight (at point of storage)',
        rightContent: <ValueWithUnit value={data?.cropWeightStorage} unit={'tonnes'} />,
      }),
      buildSimpleCalculatorTableRow({
        leftContent: 'Storage Volume Required',
        rightContent: <ValueWithUnit value={data?.volumeRequired} unit={'m3'} />,
      }),
      buildSimpleCalculatorTableRow({
        leftContent: 'What height can you store to?',
        rightContent: (
          <Select
            floating
            variant={'table'}
            buttonContent={storableHeightLabel}
            options={storableHeightOptions}
            selectedValues={storableHeight ? [storableHeight] : []}
            onOptionSelected={value => setStorableHeight(value)}
            menuClassName={'max-h-[300px]'}
            buttonClassName={'rounded-r-2xl'}
          />
        ),
        border: 'both',
        type: 'input',
      }),
      buildSimpleCalculatorTableRow({
        leftContent: 'Floor Area Required',
        rightContent: <ValueWithUnit value={data?.areaRequired} unit={'m2'} />,
      }),
    ],
    [
      areaHarvestedLabel,
      areaHarvested,
      cropId,
      data?.crop?.materialForm,
      data?.crop?.driedYield,
      data?.crop?.bulkDensity,
      data?.cropWeightDried,
      data?.cropWeightStorage,
      data?.volumeRequired,
      data?.areaRequired,
      mcLabel,
      mc,
      storableHeightLabel,
      storableHeight,
    ]
  );

  return (
    <SimpleCalculatorCard title={'Storage Requirements'}>
      <SimpleCalculatorTable values={values} />
    </SimpleCalculatorCard>
  );
};

export default StorageCalculator;
